import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const FutureOfNetworking = () => {
  useSEO({
    title: "The Future of Professional Networking | AM CARDMATE Blog",
    description: "Explore how digital technology is transforming professional networking and what the future holds for business connections.",
    image: "/assets/network.jpg",
    keywords: "future of networking, digital networking, professional connections, business trends, technology"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": "The Future of Networking: Why Paper Cards Are Obsolete",
        "description": "Explore how digital technology is transforming professional networking",
        "datePublished": "2025-05-25",
        "dateModified": "2025-05-25",
        "author": { "@type": "Person", "name": "Alina Macovei", "jobTitle": "Founder & CEO" },
        "publisher": { "@type": "Organization", "name": "AM CARDMATE", "logo": { "@type": "ImageObject", "url": "https://amcardmate.com/assets/logo.svg" } },
        "image": "https://amcardmate.com/assets/network.jpg",
        "mainEntityOfPage": { "@type": "WebPage", "@id": "https://amcardmate.com/blog/future-of-networking" }
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [selectedYear, setSelectedYear] = useState(2025);

  const timelineData = {
    1900: { title: 'The Paper Era Begins', desc: 'Calling cards become business cards. Simple name and address on cardstock.' },
    1980: { title: 'Corporate Explosion', desc: 'Logos, fancy fonts, and embossing. Still just paper with contact info.' },
    2000: { title: 'Digital Age Dawns', desc: 'Email addresses appear. Some add QR codes. Paper trying to go digital.' },
    2020: { title: 'Pandemic Accelerates Change', desc: 'Contactless everything. Digital business cards gain massive adoption.' },
    2025: { title: 'The Digital Tipping Point', desc: 'NFC cards mainstream. Real-time analytics. Paper cards feel ancient.' },
    2030: { title: 'Paper? What Paper?', desc: 'AI-powered networking. Instant global connections. Paper cards in museums.' }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-gray-900 via-blue-900 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-blue-500/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-500/10 rounded-full filter blur-3xl" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-2 text-caption text-white/70 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <span className="text-white">Future of Networking</span>
            </nav>

            <div className="text-center mb-8">
              <div className="badge badge-purple inline-flex mb-6">
                <FontAwesomeIcon icon="rocket" />
                <span>Technology Trends</span>
              </div>

              <h1 className="text-h1 text-white mb-6">
                The Future of Networking:
                <span className="block text-gradient-blue-pink mt-2">Why Paper Cards Are Obsolete</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-caption text-white/80">
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="user" className="text-yellow-400" />
                  Alina Macovei
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="calendar" className="text-yellow-400" />
                  May 25, 2025
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="clock" className="text-yellow-400" />
                  5 min read
                </span>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12">
              <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&h=600&fit=crop" alt="Future of digital networking" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-4 md:bottom-8 left-4 md:left-8 right-4 md:right-8">
                <div className="bg-black/60 backdrop-blur-md rounded-2xl p-4 md:p-6 border border-purple-500/30">
                  <h2 className="text-h4 font-bold mb-2 text-white">Welcome to 2025 and Beyond</h2>
                  <p className="text-body text-white/80">Where networking is instant, insights are real-time, and paper is history</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-body-lg text-gray-600 mb-8">
              Picture this: It's a networking event in 2030. Nobody's fumbling with paper cards. Instead, professionals are making instant connections with a tap, tracking engagement in real-time, and building meaningful relationships powered by data. This isn't science fiction – it's happening right now. 🚀
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 md:p-8 mb-12">
              <h3 className="text-h4 text-dark mb-4 flex items-center gap-3">
                <FontAwesomeIcon icon="chart-line" className="text-primary-blue" />
                The Numbers Don't Lie
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { value: '72%', label: 'of professionals prefer digital cards', color: 'text-primary-blue' },
                  { value: '88%', label: 'of paper cards thrown away', color: 'text-purple-600' },
                  { value: '5x', label: 'more connections with digital', color: 'text-pink-500' }
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className={`text-h2 font-bold ${stat.color}`}>{stat.value}</div>
                    <p className="text-body-sm text-gray-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="history" />
                </div>
                <h2 className="text-h3 text-dark">The Evolution of Business Cards</h2>
              </div>

              <div className="bg-gray-50 rounded-3xl p-6 md:p-8">
                <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8">
                  {[1900, 1980, 2000, 2020, 2025, 2030].map((year) => (
                    <button
                      key={year}
                      onClick={() => setSelectedYear(year)}
                      className={`px-4 py-2 rounded-xl text-btn font-bold transition-all ${
                        selectedYear === year
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>

                <div className="text-center px-4">
                  <h3 className="text-h3 text-dark mb-3">{timelineData[selectedYear].title}</h3>
                  <p className="text-body text-gray-600">{timelineData[selectedYear].desc}</p>
                </div>
              </div>
            </div>

            {/* Paper Problems */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="times-circle" />
                </div>
                <h2 className="text-h3 text-dark">Why Traditional Cards Are Failing</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { title: 'Static Information', desc: "Changed jobs? New phone? Time to trash thousands of cards and reprint.", gradient: 'from-red-400 to-red-600' },
                  { title: 'Zero Analytics', desc: "Did they keep your card? Call you? With paper, you're flying blind.", gradient: 'from-orange-400 to-orange-600' },
                  { title: 'Environmental Disaster', desc: "7 million trees annually for cards that get tossed.", gradient: 'from-yellow-400 to-yellow-600' },
                  { title: 'Limited Space', desc: "3.5 x 2 inches to showcase your entire professional identity?", gradient: 'from-purple-400 to-purple-600' }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300">
                    <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center text-white mb-4`}>
                      <FontAwesomeIcon icon="exclamation" />
                    </div>
                    <h3 className="text-card-title text-dark mb-2">{item.title}</h3>
                    <p className="text-card-body">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Revolution - Highlighted sections */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="badge badge-green inline-flex mb-6">
                <FontAwesomeIcon icon="rocket" />
                <span>Digital Revolution</span>
              </div>
              <h2 className="text-h2 mb-6">What's <span className="text-gradient-blue-pink">Replacing Paper</span></h2>
            </div>

            <div className="space-y-8">
              {[
                { title: '1. Instant Updates, Infinite Possibilities', desc: "Digital cards update in real-time. New role? Updated everywhere instantly.", example: "Real example: Sarah changed jobs 3 times in 2 years. With AM CARDMATE, her 500+ contacts always had her current info.", gradient: 'from-blue-500 to-purple-600' },
                { title: '2. Data-Driven Networking', desc: "Know who viewed your profile, when, and what they clicked.", example: "Power stat: AM CARDMATE users see 73% higher response rates when following up based on analytics.", gradient: 'from-purple-500 to-pink-500' },
                { title: '3. Rich Media Experiences', desc: "Video introductions, portfolio galleries, calendar booking – your digital card is a mini-website.", example: "Engagement boost: Cards with video intros get 5x more callbacks.", gradient: 'from-pink-500 to-orange-500' }
              ].map((item, i) => (
                <div key={i} className={`bg-gradient-to-r ${item.gradient} text-white rounded-3xl p-6 md:p-10`}>
                  <h3 className="text-h4 font-bold mb-4">{item.title}</h3>
                  <p className="text-body text-white/90 mb-4">{item.desc}</p>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                    <p className="text-body-sm">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center text-white">
                <FontAwesomeIcon icon="industry" />
              </div>
              <h2 className="text-h3 text-dark">Industries Leading the Change</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
              {[
                { icon: 'laptop-code', title: 'Tech & Startups', desc: '90% adoption rate. Paper cards are literally laughed at in Silicon Valley.', gradient: 'from-blue-400 to-blue-600', bg: 'from-blue-50 to-blue-100' },
                { icon: 'home', title: 'Real Estate', desc: 'Virtual tours, instant scheduling, and property portfolios on tap.', gradient: 'from-green-400 to-teal-600', bg: 'from-green-50 to-green-100' },
                { icon: 'palette', title: 'Creative Industries', desc: 'Portfolio integration and instant project sharing driving adoption.', gradient: 'from-purple-400 to-pink-600', bg: 'from-purple-50 to-purple-100' }
              ].map((item, i) => (
                <div key={i} className={`bg-gradient-to-br ${item.bg} rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300`}>
                  <div className={`w-14 h-14 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center text-white mb-4`}>
                    <FontAwesomeIcon icon={item.icon} className="text-xl" />
                  </div>
                  <h3 className="text-card-title text-dark mb-2">{item.title}</h3>
                  <p className="text-card-body">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Future Tech */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-pink-500 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="eye" />
                </div>
                <h2 className="text-h3 text-dark">What's Next</h2>
              </div>

              <div className="bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white rounded-3xl p-6 md:p-8">
                <div className="space-y-6">
                  {[
                    { icon: 'brain', title: 'AI-Powered Connections', desc: 'Smart matching algorithms that suggest valuable connections based on your goals.' },
                    { icon: 'vr-cardboard', title: 'AR/VR Integration', desc: "Point your phone at someone to see their digital card floating above them." },
                    { icon: 'globe', title: 'Global Instant Translation', desc: "Your card automatically translates to the viewer's language." },
                    { icon: 'link', title: 'Blockchain Verification', desc: "Verified professional credentials that can't be faked." }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={item.icon} className="text-xl" />
                      </div>
                      <div>
                        <h3 className="text-card-title font-bold mb-2">{item.title}</h3>
                        <p className="text-body text-white/80">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Comparison */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="balance-scale" />
                </div>
                <h2 className="text-h3 text-dark">The Choice is Clear</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-red-50 rounded-2xl p-6 md:p-8 border-2 border-red-200">
                  <h3 className="text-h4 text-red-600 mb-4">Stick with Paper</h3>
                  <div className="space-y-3">
                    {['Outdated info within months', 'No idea who kept your card', 'Contributing to deforestation', 'Limited to basic contact info', 'Constant reprinting costs'].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <FontAwesomeIcon icon="times" className="text-red-500 mt-1 flex-shrink-0" />
                        <span className="text-body text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 md:p-8 border-2 border-green-300">
                  <h3 className="text-h4 text-green-600 mb-4">Go Digital</h3>
                  <div className="space-y-3">
                    {['Always current information', 'Track every interaction', 'Eco-friendly solution', 'Rich media capabilities', 'One-time investment'].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <FontAwesomeIcon icon="check" className="text-green-500 mt-1 flex-shrink-0" />
                        <span className="text-body text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 text-white mb-4">Don't Get Left Behind</h2>
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              The future of networking is here, and it's digital. Every day you wait is another day of missed connections, lost opportunities, and wasted paper.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/personal-card" className="btn bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg">
                <FontAwesomeIcon icon="rocket" />
                <span>Start Your Digital Journey</span>
              </Link>
              <Link to="/nfc-cards" className="btn bg-white/20 text-white hover:bg-white/30 transition-all">
                <FontAwesomeIcon icon="credit-card" />
                <span>Get Your NFC Card</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Author & Related */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="border-t pt-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-body-lg flex-shrink-0">AM</div>
                <div>
                  <h3 className="text-card-title text-dark">Alina Macovei</h3>
                  <p className="text-body-sm text-gray-500 mb-2">Founder & CEO, AM CARDMATE</p>
                  <p className="text-body-sm text-gray-600">
                    Futurist, technologist, and firm believer that the best time to go digital was yesterday. Leading the charge to make paper business cards extinct by 2030!
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-dark mb-6">Continue Your Journey</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { to: '/blog/what-is-nfc-technology', title: 'What is NFC Technology?', gradient: 'from-blue-400 to-blue-600' },
                  { to: '/blog/environmental-impact-digital-cards', title: 'Environmental Impact of Digital Cards', gradient: 'from-green-400 to-teal-500' },
                  { to: '/blog/track-networking-roi', title: 'Track Your Networking ROI', gradient: 'from-teal-400 to-blue-500' }
                ].map((post, i) => (
                  <Link key={i} to={post.to} className={`group ${i === 2 ? 'sm:col-span-2 md:col-span-1' : ''}`}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`}></div>
                      <div className="p-4">
                        <h4 className="text-body font-bold text-dark group-hover:text-primary-blue transition-colors">{post.title}</h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FutureOfNetworking;